import React, { useState, useMemo, useEffect } from "react";
import "./business.scss";
import MerchantFormInput from "../Common/FormInput";
import { ReactComponent as DownArrow } from "../../../../Assets/Icons/Down Arrow.svg";
import PhoneFormInput from "../../../common/PhoneInput";
import { useNavigate } from "react-router-dom";
import Selector from "../../../common/Selector";
import { Country, State, City } from "country-state-city";
import { useFormik } from "formik";
import { businessProfileSchema } from "../../../../utils/validationSchemas";
import { useDispatch, useSelector } from "react-redux";
import {
  getMerchantBusinessProfile,
  updateMerchantBusinessProfile
} from "../../../../services/merchantService";
import { getCategories } from "../../../../services/offerService";
import GoogleMaps from "../../../common/GoogleMaps";
import PrimaryOutlineButton from "../../../common/PrimaryOutlineButton";
import PrimaryButton from "../../../common/PrimaryButton";
import Loader from "../../../common/Loader";

const BusinessProfileSection = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.merchant?.loading);
  const merchantProfile = useSelector((state) => state?.merchant?.merchantBusinessProfile);
  const merchantId = merchantProfile?._id; // Or get from props/context if needed
  const navigate = useNavigate();
  const [categories, setCategories] = useState();
  const [placeId, setPlaceId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [categoryIds, setCategoryIds] = useState([]);
  const [merchant, setMerchant] = useState();

  const handleLocationClick = () => {
    setShowMap(true);
  };

  const handleLocationSelect = (selectedLocation) => {
    setFieldValue("location", selectedLocation?.placeName);
    setPlaceId(selectedLocation?.placeId);
    // store enriched fields for submit
    setFieldValue("locationLat", selectedLocation?.lat);
    setFieldValue("locationLng", selectedLocation?.lng);
    setFieldValue("locationCity", selectedLocation?.city || "");
    setFieldValue("locationState", selectedLocation?.state || "");
    setFieldValue("locationCountry", selectedLocation?.country || "");
    setFieldValue("locationZip", selectedLocation?.zipCode || "");
  };

  useEffect(() => {
    if (!merchantProfile && merchantId) {
      dispatch(getMerchantBusinessProfile({ id: merchantId }));
    }
  }, [dispatch, merchantId, merchantProfile]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: merchantProfile?.businessName || "",
      address: merchantProfile?.address || "",
      website: merchantProfile?.website || "",
      logo: merchantProfile?.logo || "",
      contactPersonNumber: merchantProfile?.contactPersonNumber || "",
      contactPersonName: merchantProfile?.contactPersonName || "",
      aboutUs: merchantProfile?.aboutUs || "",
      category: merchantProfile?.category?.map((cat) => cat?._id) || [],
      email: merchantProfile?.user?.email || "",
      locationLat: "",
      locationLng: "",
      locationCity: "",
      locationState: "",
      locationCountry: "",
      locationZip: "",
    },
    validationSchema: businessProfileSchema,
    onSubmit: async (values) => {
      const payload = { ...values };
      // Only send fields that have changed or are filled
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] == null) delete payload[key];
      });
      // Attach locations array when lat/lng present
      if (values?.locationLat && values?.locationLng) {
        payload.locations = [
          {
            address: values?.location || values?.address,
            city: values?.locationCity,
            state: values?.locationState,
            country: values?.locationCountry,
            zipCode: values?.locationZip,
            lat: Number(values?.locationLat),
            lng: Number(values?.locationLng),
          },
        ];
      }
      const result = await dispatch(updateMerchantBusinessProfile({ id: merchantId, data: payload }))?.unwrap();
      if (result?.success) {
        // Optionally navigate or show success
      }
    },
  });

  const {
    businessName,
    address,
    website,
    logo,
    contactPersonNumber,
    contactPersonName,
    aboutUs,
    category,
    email,
  } = formik.values;

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    errors,
    touched,
    setValues,
  } = formik;

  const getMerchantCategoriesData = async () => {
    const categoryPromise = await dispatch(getCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      setCategories(categoryPromise?.data);
    }
  };

  const merchantCategoryOptions = categories?.map((cat) => ({
    value: cat?.title ?? "abc",
    label: cat?.title ?? "dsc",
  }));

  useEffect(() => {
    getMerchantCategoriesData();
  }, []);

  const handleCategoryChange = (e) => {
    if (e) {
      setFieldValue("merchantCategory", e);
    }
  };

  useEffect(() => {
    if (merchantProfile?.category?.length !== 0 && categories?.length !== 0) {
      const catIds = merchantProfile?.category?.map((merchantCategoryName) => {
        const matchingCategories = categories?.filter(
          (cat) => cat?.title === merchantCategoryName
        );
        return matchingCategories?.map((cat) => cat?._id);
      });
      setCategoryIds(catIds?.flat());
    }
  }, [merchantProfile?.category, categories]);

  useEffect(() => {
    if (merchantProfile !== undefined && merchantProfile !== null) {
      setPlaceId(merchantProfile?.location?.googlePlaceId);
      setValues({
        businessName: merchantProfile?.businessName,
        address: merchantProfile?.address,
        website: merchantProfile?.website,
        logo: merchantProfile?.logo,
        contactPersonNumber: merchantProfile?.contactPersonNumber,
        contactPersonName: merchantProfile?.contactPersonName,
        aboutUs: merchantProfile?.aboutUs,
        category: merchantProfile?.category?.map((cat) => cat?.title),
        email: merchantProfile?.user?.email,
      });
    }
  }, [merchantProfile]);

  return (
    <div className="px-9 py-10">
      {loading === true && <Loader />}
      <div className="">
        <p className="lg:text-[20px] sm:text-[18px] font-[500] text-[#303030] font-poppins">
          Business profile
        </p>
      </div>

      <MerchantFormInput
        label={"Business Name"}
        width={"100%"}
        name="businessName"
        value={businessName}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.businessName}
        touched={touched.businessName}
      />

      <MerchantFormInput
        label={"Location"}
        width={"100%"}
        name="address"
        value={address}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.address}
        touched={touched.address}
        onClick={handleLocationClick}
      />
      {showMap && (
        <GoogleMaps
          onLocationSelect={handleLocationSelect}
          searchContainerClass="sm:w-[35%] md:w-[54%] lg:w-[60%] xl:w-[60%] 2xl:w-[64%] sm:left-[50%] md:left-[35%] lg:left-[29%] xl:left-[28%] 2xl:left-[24%]"
        />
      )}

      <MerchantFormInput
        label={"Website URL"}
        width={"100%"}
        name="website"
        value={website}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.website}
        touched={touched.website}
      />

      <MerchantFormInput
        label={"Logo (image name or URL)"}
        width={"100%"}
        name="logo"
        value={logo}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.logo}
        touched={touched.logo}
      />

      <MerchantFormInput
        label={"Contact Person Name"}
        width={"100%"}
        name="contactPersonName"
        value={contactPersonName}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.contactPersonName}
        touched={touched.contactPersonName}
      />

      <MerchantFormInput
        label={"Contact Person Number"}
        width={"100%"}
        name="contactPersonNumber"
        value={contactPersonNumber}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.contactPersonNumber}
        touched={touched.contactPersonNumber}
      />

      <MerchantFormInput
        label={"About Business"}
        width={"100%"}
        name="aboutUs"
        value={aboutUs}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.aboutUs}
        touched={touched.aboutUs}
      />

      <div className="selectBusiness">
        <Selector
          value={category}
          label={"Merchant Category"}
          labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
          divClassName="mt-5 w-full multipleField"
          selectClassName="w-full rounded-[8px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none !border-none shadow-[0_4px_4px_0px_rgba(0,0,0,0.06)]"
          flexClassName="gap-1"
          options={merchantCategoryOptions}
          suffixIcon={<DownArrow />}
          placeholder={"Select Category"}
          onChange={(values) => setFieldValue("category", values)}
          handleBlur={handleBlur}
          error={errors.category}
          touched={touched.category}
          multiple={true}
        />
      </div>

      <MerchantFormInput
        label={"Email (read-only)"}
        width={"100%"}
        name="email"
        value={email}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.email}
        touched={touched.email}
        readOnly
      />

      <div className=" flex justify-between text-end mt-8 w-full">
        <PrimaryOutlineButton
          label={"Save And Exit"}
          className={
            "lg:w-[30%] 2xl:w-[25%] sm:w-[35%] lg:h-[50px] sm:h-[50px] rounded-[5px]"
          }
          onClick={handleSubmit}
        />
        <PrimaryButton
          className={
            "lg:w-[30%] 2xl:w-[25%] sm:w-[35%] lg:h-[50px] sm:h-[50px] rounded-[5px] lg:text-[18px] sm:text-[14px] font-[500]"
          }
          label="Continue"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default BusinessProfileSection;
